/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Left Accordion List Component
 *
 * @file LeftAccordionList.jsx
 * @author Murhsid Ahamedh
 */

import React from 'react';
import PropTypes from 'prop-types';
import { injectIntl, intlShape } from 'react-intl';
import Box from '@mui/material/Box';
import CustomAccordion from '../../common/CustomAccordion';
import NamedLabelChip from '../../common/NamedLabelChip';
import {
  LeftAccordionContainer,
  LeftAccordionItemRow,
  LeftAccordionTopicText,
  LeftAccordionDoneIcon,
  LeftAccordionTopicsList,
  LeftAccordionEmptyState
} from './styles/LeftAccordionListStyle';
import messages from '../../../common/defaultMessages';
import { NEEDS_REVIEW_SECTION, SELECTION_TYPE } from '../../../common/constants';

const createSelectionKey = (sectionName, type, label = '', chapterTitle = '') => `${sectionName}::${type}::${label}::${chapterTitle}`;

const parseSelectionKey = (key) => {
  const parts = key.split('::');

  return {
    section: parts[0] || '',
    type: parts[1] || SELECTION_TYPE.TOPIC,
    label: parts[2] || '',
    chapterTitle: parts[3] || ''
  };
};

const getTopicName = (topic) => (typeof topic === 'string' ? topic : (topic?.name || ''));


const LeftAccordionList = ({ data, onSelect, intl }) => {
  const sections = Array.isArray(data) ? data : [];

  const getDefaultSelectionKey = React.useCallback(() => {
    const needsReviewIndex = sections.findIndex(
      (section) => (section.id || '').toLowerCase() === NEEDS_REVIEW_SECTION.toLowerCase()
    );

    const orderedSections = needsReviewIndex >= 0
      ? [sections[needsReviewIndex], ...sections.slice(0, needsReviewIndex), ...sections.slice(needsReviewIndex + 1)]
      : sections;

    // eslint-disable-next-line no-restricted-syntax
    for (const section of orderedSections) {
      const topics = Array.isArray(section?.topics) ? section.topics : [];
      if (topics.length > 0) {
        const firstTopicName = getTopicName(topics[0]);
        if (firstTopicName) {
          return createSelectionKey(section.id, SELECTION_TYPE.TOPIC, firstTopicName, topics[0]?.chapterTitle || '');
        }
      }
    }

    return createSelectionKey(NEEDS_REVIEW_SECTION, SELECTION_TYPE.TOPIC, '', '');
  }, [sections]);

  const [selectedKey, setSelectedKey] = React.useState(getDefaultSelectionKey);

  React.useEffect(() => {
    if (!onSelect || !selectedKey) return;
    const selection = parseSelectionKey(selectedKey);
    if (selection.section) {
      onSelect(selection);
    }
  }, [selectedKey, onSelect]);

  const handleTopicSelect = (sectionName, topicName, chapterTitle) => {
    const key = createSelectionKey(sectionName, SELECTION_TYPE.TOPIC, topicName, chapterTitle);
    setSelectedKey(key);
  };

  const getTopicKey = (section) => {
    const topics = Array.isArray(section?.topics) ? section.topics : [];
    if (topics.length > 0) {
      const firstTopicName = getTopicName(topics[0]);
      if (firstTopicName) {
        return createSelectionKey(section.id, SELECTION_TYPE.TOPIC, firstTopicName, topics[0]?.chapterTitle);
      }
    }

    return createSelectionKey(section.id, SELECTION_TYPE.TOPIC, '', '');
  };

  const renderTopicItem = (section, topic) => {
    const topicName = getTopicName(topic);
    const key = createSelectionKey(section.id, SELECTION_TYPE.TOPIC, topicName, topic?.chapterTitle || '');
    const isSelected = selectedKey === key;

    return (
      <LeftAccordionItemRow
        key={topicName}
        component="li"
        role="button"
        tabIndex={0}
        isSelected={isSelected}
        onClick={() => handleTopicSelect(section.id, topicName, topic?.chapterTitle || '')}
        onKeyPress={(e) => e.key === 'Enter' && handleTopicSelect(section.id, topicName, topic?.chapterTitle || '')}
      >
        <LeftAccordionTopicText
          component="span"
          isSelected={isSelected}
        >
          {topicName}
        </LeftAccordionTopicText>
        {isSelected && <LeftAccordionDoneIcon />}
      </LeftAccordionItemRow>
    );
  };

  const renderTopicsList = (section) => {
    const hasTopics = section.topics?.length > 0;

    if (!hasTopics) {
      return (
        <LeftAccordionEmptyState>
          <Box component="span">{intl.formatMessage(messages.noTopicsAtThisTime)}</Box>
        </LeftAccordionEmptyState>
      );
    }

    return (
      <LeftAccordionTopicsList component="ul">
        {section.topics.map((topic) => renderTopicItem(section, topic))}
      </LeftAccordionTopicsList>
    );
  };

  return (
    <LeftAccordionContainer>
      {sections.map((section) => (
        <CustomAccordion
          key={section.id}
          title={<NamedLabelChip name={section.name} count={section.count} />}
          defaultExpanded={selectedKey === getTopicKey(section)}
          backgroundColor="#FFFFFF"
          caretColor="#6F3FF5"
          cardStyle={{
            boxShadow: '0 1px 0 rgba(0,0,0,0.06)',
            borderRadius: 12,
            border: '1px solid rgba(0,0,0,0.06)'
          }}
        >
          <Box>
            {renderTopicsList(section)}
          </Box>
        </CustomAccordion>
      ))}
    </LeftAccordionContainer>
  );
};

LeftAccordionList.propTypes = {
  data: PropTypes.array,
  onSelect: PropTypes.func,
  intl: intlShape.isRequired
};

LeftAccordionList.defaultProps = {
  data: [],
  onSelect: () => {}
};

export default injectIntl(LeftAccordionList);
